import React, { ChangeEvent, useState } from "react";
import { UploadCloud, X } from "lucide-react";
import { error, success } from "../../../../utils/toast";
import apiMethods from "../../../../utils/api";

interface AvatarUploadProps {
    userId: string;
    currentAvatar?: string;
    onAvatarChange?: (avatarUrl: string) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB for avatars
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const AvatarUpload = ({ userId, currentAvatar, onAvatarChange }: AvatarUploadProps) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState<string | null>(currentAvatar || null);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            error("Chỉ chấp nhận ảnh (JPEG, PNG, WebP, GIF)");
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            error("Kích thước ảnh không được vượt quá 5MB");
            return;
        }

        setUploading(true);

        try {
            // Step 0: Delete old avatar if exists
            if (currentAvatar) {
                try {
                    // Get all public files for this user (avatars)
                    const userFiles: any = await apiMethods.get(`/files/?uploader=${userId}&is_public=true&include_public=true`);
                    if (userFiles.results && userFiles.results.length > 0) {
                        // Delete all old avatar files
                        for (const oldFile of userFiles.results) {
                            try {
                                await apiMethods.delete(`/files/${oldFile.id}/`);
                            } catch (err) {
                                console.error(`Failed to delete avatar ${oldFile.id}:`, err);
                            }
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch old avatars:", err);
                    // Continue with upload even if deletion fails
                }
            }

            // Step 1: Initiate upload (is_public=true for avatars)
            const initiateResponse: any = await apiMethods.post("/files/initiate/", {
                model: "account.User",
                object_id: userId,
                filename: file.name,
                size_bytes: file.size,
                mime_type: file.type,
                is_public: true, // Avatars are public
            });

            const { file_id, put_sas_url, required_headers } = initiateResponse;

            // Step 2: Upload to Azure
            const azureResponse = await fetch(put_sas_url, {
                method: "PUT",
                headers: required_headers,
                body: file,
            });

            if (!azureResponse.ok) {
                const errorText = await azureResponse.text();
                throw new Error(`Upload to Azure failed: ${azureResponse.status} - ${errorText}`);
            }

            // Step 3: Calculate checksum
            const arrayBuffer = await file.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const checksum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            // Step 4: Complete upload with checksum
            await apiMethods.post("/files/complete/", {
                file_id,
                checksum,
            });

            // Step 5: Link file to user
            await apiMethods.post(`/files/${file_id}/link/`, {
                model: "account.User",
                object_id: userId,
            });

            // Step 6: Fetch user data to get the avatar URL from backend serializer
            const userResponse: any = await apiMethods.get(`/users/${userId}/`);
            const newAvatarUrl = userResponse.avatar;
            
            // Set preview to the direct Azure blob URL
            setPreview(newAvatarUrl);
            
            if (onAvatarChange) {
                onAvatarChange(newAvatarUrl);
            }
            
            // Update localStorage immediately to refresh navbar
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                user.avatar = newAvatarUrl;
                localStorage.setItem('user', JSON.stringify(user));
                window.dispatchEvent(new Event('storage'));
            }

            success("THÀNH CÔNG", "Tải ảnh đại diện thành công");
        } catch (err: any) {
            console.error("Upload error:", err);
            error("Tải lên ảnh đại diện thất bại", err?.message || "");
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setPreview(null);
        if (onAvatarChange) {
            onAvatarChange("");
        }
    };

    return (
        <div className="tw-space-y-3">
            <div className="tw-flex tw-items-center tw-gap-4">
                {/* Avatar Preview */}
                {preview ? (
                    <div className="tw-relative tw-group">
                        <img
                            src={preview}
                            alt="Avatar"
                            className="tw-w-20 tw-h-20 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200"
                        />
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="tw-absolute tw-top-0 tw-right-0 tw-w-6 tw-h-6 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-cursor-pointer"
                        >
                            <X className="tw-w-4 tw-h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="tw-w-20 tw-h-20 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-dashed tw-border-gray-300">
                        <UploadCloud className="tw-w-8 tw-h-8 tw-text-gray-400" />
                    </div>
                )}

                {/* Upload Button */}
                <label
                    className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-border tw-text-sm tw-transition ${
                        uploading
                            ? "tw-text-gray-400 tw-border-gray-200 tw-cursor-not-allowed"
                            : "tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-50 tw-cursor-pointer"
                    }`}
                >
                    <UploadCloud className="tw-w-4 tw-h-4" />
                    <span>{uploading ? "Đang tải..." : preview ? "Thay đổi" : "Tải lên"}</span>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="tw-hidden"
                        disabled={uploading}
                    />
                </label>
            </div>

            <p className="tw-text-xs tw-text-gray-500">
                Chấp nhận: JPEG, PNG, WebP, GIF. Tối đa 5MB.
            </p>
        </div>
    );
};

export default AvatarUpload;

